import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/pages/Dashboard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { apiClient } from "@/App";
import { toast } from "sonner";

export const InvoiceSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [settings, setSettings] = useState({
    business_name: "",
    full_name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    postcode: "",
    country: "UK",
    phone: "",
    email: "",
    bank_name: "",
    account_name: "",
    account_number: "",
    sort_code: "",
    payment_terms_days: 14,
    vat_registered: false,
    vat_number: "",
    next_invoice_number: ""
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get("/user/invoice-settings");
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error("Failed to fetch settings", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await apiClient.put("/user/invoice-settings", settings);
      toast.success("Settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Layout>
      <div data-testid="invoice-settings-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate("/invoices")} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-1">Settings</p>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Invoice Settings</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Configure your default invoice details
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
            {/* Personal/Business Details */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg">Your Details</CardTitle>
                <CardDescription>This appears on your invoices</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Business/Trading Name (optional)</Label>
                  <Input
                    value={settings.business_name}
                    onChange={(e) => updateField("business_name", e.target.value)}
                    data-testid="business-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={settings.full_name}
                    onChange={(e) => updateField("full_name", e.target.value)}
                    required
                    data-testid="full-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={settings.email}
                      onChange={(e) => updateField("email", e.target.value)}
                      data-testid="email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={settings.phone}
                      onChange={(e) => updateField("phone", e.target.value)}
                      data-testid="phone"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Address */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg">Address</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Address Line 1</Label>
                  <Input
                    value={settings.address_line1}
                    onChange={(e) => updateField("address_line1", e.target.value)}
                    data-testid="address-line1"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Address Line 2</Label>
                  <Input
                    value={settings.address_line2}
                    onChange={(e) => updateField("address_line2", e.target.value)}
                    data-testid="address-line2"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={settings.city}
                      onChange={(e) => updateField("city", e.target.value)}
                      data-testid="city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Postcode</Label>
                    <Input
                      value={settings.postcode}
                      onChange={(e) => updateField("postcode", e.target.value)}
                      data-testid="postcode"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Input
                      value={settings.country}
                      onChange={(e) => updateField("country", e.target.value)}
                      data-testid="country"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bank Details */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg">Bank Details</CardTitle>
                <CardDescription>For payment instructions on invoices</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Bank Name</Label>
                  <Input
                    value={settings.bank_name}
                    onChange={(e) => updateField("bank_name", e.target.value)}
                    data-testid="bank-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Account Name</Label>
                  <Input
                    value={settings.account_name}
                    onChange={(e) => updateField("account_name", e.target.value)}
                    data-testid="account-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Sort Code</Label>
                    <Input
                      value={settings.sort_code}
                      onChange={(e) => updateField("sort_code", e.target.value)}
                      data-testid="sort-code"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Account Number</Label>
                    <Input
                      value={settings.account_number}
                      onChange={(e) => updateField("account_number", e.target.value)}
                      data-testid="account-number"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Terms */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg">Payment Terms</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Default Payment Terms (days)</Label>
                  <Input
                    type="number"
                    value={settings.payment_terms_days}
                    onChange={(e) => updateField("payment_terms_days", parseInt(e.target.value) || 14)}
                    min="0"
                    max="365"
                    className="w-32"
                    data-testid="payment-terms-days"
                  />
                  <p className="text-xs text-muted-foreground">
                    Due date will be calculated as issue date + this many days
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Invoice Numbering */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg">Invoice Numbering</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Next invoice number</Label>
                  <Input
                    value={settings.next_invoice_number || ""}
                    onChange={(e) => updateField("next_invoice_number", e.target.value)}
                    placeholder="e.g. Invoice 2026 01"
                    data-testid="next-invoice-number"
                  />
                  <p className="text-xs text-muted-foreground">
                    Sets the number for your <em>next</em> invoice. After that, Fretless auto-increments
                    (e.g. <code>Invoice 2026 01 → Invoice 2026 02</code>). Leave blank to keep
                    incrementing from your last invoice. You can also override the number per-invoice on the form.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* VAT */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg">VAT Registration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>VAT Registered</Label>
                    <p className="text-xs text-muted-foreground">Enable if you're VAT registered</p>
                  </div>
                  <Switch
                    checked={settings.vat_registered}
                    onCheckedChange={(checked) => updateField("vat_registered", checked)}
                    data-testid="vat-registered"
                  />
                </div>
                {settings.vat_registered && (
                  <div className="space-y-2">
                    <Label>VAT Number</Label>
                    <Input
                      value={settings.vat_number}
                      onChange={(e) => updateField("vat_number", e.target.value)}
                      data-testid="vat-number"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex justify-end gap-4">
              <Button type="button" variant="outline" onClick={() => navigate("/invoices")}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="save-settings"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Layout>
  );
};

export default InvoiceSettings;
